import { MessagingSdk } from 'lib-klab-sdks';
import { ChatMessageReplyUI, ChatMessageUI, ChatUploadMediaKind } from '../../../domain/models/messaging/chat-ui.model';
import { normalizeChatUploadMime, resolveChatFileKind } from '../../../domain/models/messaging/chat-file-kind.util';
import { colorForUserId } from './chat-color.util';
import { formatChatDisplayName, resolveLiveChatSenderName } from './chat-display-name.util';
import { formatChatTime } from './time-format.util';
import { ChatMediaUrlPolicy } from '../../services/chat-media-url-policy.service';

let optimisticMessageSequence = 0;

/**
 * Maps a server-pushed {@code chat:message:new} payload into a {@link ChatMessageUI}.
 *
 * <p>Status rules (palomitas):
 * <ul>
 *   <li>{@code deliveredToAllAt == null} → {@code 'sent'} (1 palomita)</li>
 *   <li>{@code deliveredToAllAt != null} → {@code 'delivered'} (2 palomitas)</li>
 *   <li>{@code 'read'} is set later via {@code chat:message:read:update}.</li>
 * </ul>
 *
 * <p>Si el payload trae replyTo, se mapea como ChatMessageReplyUI para que
 * la burbuja renderice el quote del mensaje original.
 */
export function mapNewMessageToUi(
  payload: MessagingSdk.NewMessagePayload,
  currentUserId: number,
  mediaUrlPolicy?: ChatMediaUrlPolicy,
  liveDisplayName?: string | null,
): ChatMessageUI {
  const isOwn = payload.sender?.userId === currentUserId;
  const text = extractText(payload);
  const media = mapMediaFields(payload.type, payload.content, mediaUrlPolicy);

  return {
    id: payload.messageId,
    sequence: payload.sequence ?? undefined,
    clientMessageId: payload.clientMessageId ?? null,
    senderId: String(payload.sender?.userId ?? ''),
    senderName: resolveLiveChatSenderName({
      participantDisplayName: readSenderDisplayName(payload.sender) ?? liveDisplayName,
      fullName: payload.sender?.fullName,
    }),
    senderColor: colorForUserId(payload.sender?.userId ?? 0),
    senderSnapshot: payload.sender ? { ...payload.sender } : undefined,
    content: text,
    mentions: payload.content?.mentions ?? null,
    type: payload.type,
    ...media,
    timestamp: formatChatTime(payload.sentAt),
    status: payload.deliveredToAllAt ? 'delivered' : 'sent',
    isOwn,
    replyTo: mapReplyTo(payload.replyTo),
    forwarded: payload.forwarded ?? null,
    reactions: cloneReactions(payload.reactions),
    reactionsRevision: normalizeReactionRevision(payload.reactionsRevision),
    serverSentAt: payload.sentAt,
    albumId: payload.album?.albumId ?? null,
    albumIndex: payload.album?.albumIndex ?? null,
    albumCount: payload.album?.albumCount ?? null,
  };
}

export function mapHistoryMessageToUi(
  payload: MessagingSdk.MessageHistoryRecord,
  currentUserId: number,
  mediaUrlPolicy?: ChatMediaUrlPolicy,
): ChatMessageUI {
  const isDeleted = Boolean(payload.deletedForAllAt);
  const media = mapMediaFields(payload.type, payload.content, mediaUrlPolicy);
  return {
    id: payload.messageId,
    sequence: payload.sequence ?? undefined,
    clientMessageId: payload.clientMessageId ?? null,
    senderId: String(payload.sender?.userId ?? ''),
    senderName: formatChatDisplayName({
      displayName: readSenderDisplayName(payload.sender) ?? undefined,
      fullName: payload.sender?.fullName,
    }),
    senderColor: colorForUserId(payload.sender?.userId ?? 0),
    senderSnapshot: payload.sender ? { ...payload.sender } : undefined,
    content: isDeleted ? 'Mensaje eliminado' : extractHistoryText(payload),
    mentions: isDeleted ? null : (payload.content?.mentions ?? null),
    type: payload.type,
    ...media,
    timestamp: formatChatTime(payload.sentAt),
    status: payload.aggregateStatus ?? (payload.deliveredToAllAt ? 'delivered' : 'sent'),
    isOwn: payload.sender?.userId === currentUserId,
    replyTo: mapReplyTo(payload.replyTo),
    forwarded: payload.forwarded ?? null,
    isEdited: Boolean(payload.editedAt),
    isDeleted,
    reactions: isDeleted ? [] : cloneReactions(payload.reactions),
    reactionsRevision: normalizeReactionRevision(payload.reactionsRevision),
    serverSentAt: payload.sentAt,
    albumId: payload.album?.albumId ?? null,
    albumIndex: payload.album?.albumIndex ?? null,
    albumCount: payload.album?.albumCount ?? null,
  };
}

/**
 * Optimistic local representation of a text message the user just sent.
 * Status starts as {@code 'sent'} (1 palomita) until the server confirms delivery.
 *
 * <p>Used to render the message immediately after emit, before the server
 * round-trip arrives via {@code chat:message:new} broadcast.
 *
 * <p>Opcionalmente acepta {@code replyTo} para que el optimistic update
 * ya muestre el quote sin esperar el echo del server.
 */
export function buildOptimisticTextMessage(
  conversationId: string,
  text: string,
  currentUserId: number,
  clientMessageId: string,
  replyTo?: ChatMessageReplyUI | null,
  mentions: MessagingSdk.MessageMention[] = [],
): ChatMessageUI {
  const createdAtMs = Date.now();
  // Temporary client-side id; replaced when the server's own id arrives.
  const tempId = `local-${clientMessageId}-${createdAtMs}-${optimisticMessageSequence++}`;
  return {
    id: tempId,
    clientMessageId,
    senderId: String(currentUserId),
    content: text,
    mentions,
    timestamp: formatChatTime(new Date(createdAtMs).toISOString()),
    status: 'sent',
    isOwn: true,
    reactions: [],
    reactionsRevision: 0,
    replyTo: replyTo ?? undefined,
    optimisticCreatedAtMs: createdAtMs,
  };
}

/** Optional upload-lifecycle fields for an optimistic media message still in flight — see {@link ChatMessageUI.uploadState}. */
export interface OptimisticMediaUploadState {
  clientFileId?: string;
  uploadState?: 'uploading' | 'finalizing' | 'failed' | 'cancelled' | 'interrupted' | null;
  uploadProgress?: number | null;
  mediaKind?: ChatUploadMediaKind | null;
  durationMs?: number | null;
}

/**
 * Lo único que este mapper necesita saber del archivo. Un `File` real lo
 * satisface estructuralmente, así que los llamadores que sí tienen los bytes no
 * cambian; y una subida rehidratada tras una recarga —donde el `File` ya no
 * existe— puede armar el descriptor desde el registro persistido.
 */
export interface OptimisticMediaFileDescriptor {
  name: string;
  type: string;
  size: number;
}

export function buildOptimisticMediaMessage(
  conversationId: string,
  file: OptimisticMediaFileDescriptor,
  s3Key: string,
  currentUserId: number,
  clientMessageId: string,
  replyTo?: ChatMessageReplyUI | null,
  localObjectUrl?: string,
  captionText?: string | null,
  mediaUrlPolicy?: ChatMediaUrlPolicy,
  uploadState?: OptimisticMediaUploadState,
  /**
   * Momento de creación a usar en lugar de `Date.now()`. Solo para placeholders
   * rehidratados, que deben conservar el instante original de la subida en vez
   * de nacer con la marca de tiempo de la recarga.
   */
  createdAtMsOverride?: number,
  /** Metadata de álbum de imágenes (MOMSG-1172). null si el mensaje no pertenece a un álbum. */
  album?: MessagingSdk.AlbumAssociation | null,
): ChatMessageUI {
  const createdAtMs = createdAtMsOverride ?? Date.now();
  const type = resolveChatFileKind(file.name, file.type) === 'image' ? 'IMAGE' : 'FILE';
  const mimeType = normalizeChatUploadMime(file.name, file.type);
  const mediaKind = uploadState?.mediaKind
    ?? (mimeType.startsWith('audio/') ? 'ATTACHED_AUDIO' : 'GENERAL');
  const tempId = `local-${clientMessageId}-${createdAtMs}-${optimisticMessageSequence++}`;
  // s3Key puede llegar vacío: el mensaje optimista se crea ANTES de que
  // exista el key real (recién arranca la subida) — sin key todavía no hay
  // downloadUrl/imageUrl resoluble, la tarjeta se renderiza según
  // uploadState en vez de por presencia de URL.
  const url = s3Key ? mediaUrlPolicy?.resolvePersisted(s3Key) ?? null : null;
  const ownedPreview = mediaUrlPolicy?.resolveRenderable(localObjectUrl) ?? null;
  return {
    id: tempId,
    clientMessageId,
    senderId: String(currentUserId),
    content: captionText?.trim() ?? '',
    type,
    s3Key: s3Key || null,
    url,
    mimeType,
    mediaKind,
    durationMs: uploadState?.durationMs ?? null,
    fileName: file.name,
    fileSize: file.size,
    imageUrl: type === 'IMAGE' ? ownedPreview ?? url : null,
    downloadUrl: type === 'FILE' && (mediaKind === 'VOICE_NOTE' || mediaKind === 'ATTACHED_AUDIO')
      ? ownedPreview ?? url
      : url,
    timestamp: formatChatTime(new Date(createdAtMs).toISOString()),
    status: 'sent',
    isOwn: true,
    reactions: [],
    reactionsRevision: 0,
    replyTo: replyTo ?? undefined,
    optimisticCreatedAtMs: createdAtMs,
    localObjectUrl: ownedPreview ?? undefined,
    clientFileId: uploadState?.clientFileId ?? null,
    uploadState: uploadState?.uploadState ?? null,
    uploadProgress: uploadState?.uploadProgress ?? null,
    albumId: album?.albumId ?? null,
    albumIndex: album?.albumIndex ?? null,
    albumCount: album?.albumCount ?? null,
  };
}

export function generateClientMessageId(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  const randomPart = Math.random().toString(36).slice(2, 12);
  return `cmid-${Date.now()}-${randomPart}`;
}

function cloneReactions(
  reactions: readonly MessagingSdk.MessageReaction[] | null | undefined,
): MessagingSdk.MessageReaction[] {
  return (reactions ?? []).map((reaction) => ({ ...reaction }));
}

function normalizeReactionRevision(revision: number | null | undefined): number {
  return Number.isFinite(revision) && revision! >= 0 ? revision! : 0;
}

function mapReplyTo(
  reply: MessagingSdk.ReplyTo | null | undefined,
): ChatMessageReplyUI | undefined {
  if (!reply) return undefined;
  const isUnavailable = reply.contentPreview === 'Message unavailable';
  return {
    messageId: reply.messageId,
    senderId: String(reply.senderId),
    senderName: isUnavailable ? 'Mensaje no disponible' : undefined,
    contentPreview: isUnavailable ? 'Mensaje no disponible' : (reply.contentPreview ?? ''),
    isUnavailable,
  };
}

function readSenderDisplayName(sender: MessagingSdk.NewMessagePayload['sender'] | null | undefined): string | null {
  const displayName = (sender as { displayName?: unknown } | null | undefined)?.displayName;
  return typeof displayName === 'string' && displayName.trim().length > 0 ? displayName : null;
}

function extractText(payload: MessagingSdk.NewMessagePayload): string {
  if (payload.type === 'TEXT') {
    return payload.content?.text ?? '';
  }
  return payload.content?.text ?? '';
}

function extractHistoryText(payload: MessagingSdk.MessageHistoryRecord): string {
  if (payload.type === 'TEXT') {
    return payload.content?.text ?? '';
  }
  return payload.content?.text ?? '';
}

function mapMediaFields(
  type: MessagingSdk.MessageType | 'SYSTEM',
  content: MessagingSdk.MessageContent | undefined,
  mediaUrlPolicy?: ChatMediaUrlPolicy,
): Partial<ChatMessageUI> {
  if (type !== 'FILE' && type !== 'IMAGE') return {};
  const contentRecord = content as (MessagingSdk.MessageContent & { url?: string | null; fileUrl?: string | null }) | undefined;
  const s3Key = content?.s3Key ?? null;
  const url = [contentRecord?.url, contentRecord?.fileUrl, content?.reference, s3Key]
    .map((candidate) => mediaUrlPolicy?.resolvePersisted(candidate) ?? null)
    .find((candidate): candidate is string => !!candidate) ?? null;
  return {
    s3Key,
    url,
    mimeType: content?.mimeType ?? content?.actualMime ?? null,
    mediaKind: content?.mediaKind ?? null,
    durationMs: content?.durationMs ?? null,
    fileName: content?.fileName ?? null,
    fileSize: content?.fileSize ?? null,
    imageUrl: type === 'IMAGE' ? url : null,
    downloadUrl: url,
  };
}
